// 14-B2 (R37/R42's pattern: MOVE THE DATA, NOT THE COMPONENT) — **WHAT THE HOUSE MAKES FOR A NEW
// MEMBER, AND WHEN.** Three rosters that decide what My Room's report slots offer, which of them are
// already written when she first walks in, and which are hers to ask for.
//
// They were sixteen lines of pure data inside `MyRoomPage`, a component with thirty hooks in it, so
// nothing could load them — every check on this roster was a REGEX over App.tsx (`smoke.mjs` matches
// `signupSetReports.some(` and `firstTapReports` as strings). Out here a gate imports the real arrays
// and holds each `type` against the rosters that own it. The identifiers keep their names, because
// those source checks read the CALL SITES in App.tsx and an extraction must never read as a deletion
// (R9's lesson, paid five times on this line).
//
// It imports NOTHING, which is the entry requirement that lets a `.mjs` gate load it.
//
// **THE `label` IS THE HOUSE'S OWN COPY AND IT IS NOT IN THE STRINGS CATALOG, ON PURPOSE.** A report
// is NAMED by `REPORT_TYPE_LABELS` (R32) and by the server beside it; naming one here in Spanish
// would be a second answer key for a name the ledger, the shelf and the PDF already agree on. Report
// names travel with part (d), where server copy is translated, and they travel together.

export type RoomReportSlot = { type: string; label: string };

// N18-1: THE SIGNUP SET — written in parallel at room creation and complete before she can enter, so
// these two are already waiting for her the moment she walks in.
export const signupSetReports: RoomReportSlot[] = [
  { type: "birth-chart", label: "Birth Chart" },
  { type: "numerology", label: "Numerology" },
];

// Human Design is FREE but not pre-written: its card invites the tap and says so honestly, rather
// than claiming a reading that has not been written yet.
export const firstTapReports: RoomReportSlot[] = [
  { type: "human-design", label: "Human Design" },
];

// On-demand reports the member generates from here with one click; never auto-generated (R3).
export const onDemandReports: RoomReportSlot[] = [
  { type: "personality", label: "Personality & Purpose" },
  { type: "compatibility", label: "Compatibility Profile" },
];

// What her room EXPECTS to hold once the door opens: the set written at signup plus the free one
// waiting for a tap. Derived, never a fourth hand-typed list (R31).
export const expectedReports: RoomReportSlot[] = [...signupSetReports, ...firstTapReports];

// CE-1 (owner, 2026-08-09) — **WHAT A FOUNDATIONAL SLOT MAY SAY, AND READINESS COMES FROM THE ROW
// AND FROM NOTHING ELSE.**
//
// **HER DEFECT, NAMED:** the report strip rendered its `found` branch when a row existed and fell
// through to a branch that painted `report-chip--ready` and said **"Ready to open"** — and that
// branch is reached PRECISELY BECAUSE THE ROW IS MISSING. The only thing that ever suppressed it was
// `buildingActive`, a `sessionStorage` deadline that is true for a few minutes in ONE browser
// session, so on any later visit, any reload and any other device a member with no birth chart was
// told her birth chart was ready to open. **296 members and 509 slots were saying it** when this was
// measured. Nothing in that branch ever asked whether the reading exists.
//
// **SO READINESS IS ONE FIELD.** `hasRow` is the report row itself and it is the ONLY input that can
// return `"open"`; every other state is a different SENTENCE about the same fact, which is that
// there is no reading here. Collapsing them is what caused this: *the build is running*, *this one
// is written when you ask* and *this one did not arrive* are three different facts (R18) and only
// the third is a defect the member needs to be told about.
//
// It lives here rather than inside `MyRoomPage` because that component holds thirty hooks and
// nothing could ever RUN this decision — which is exactly why a lie sat in it. This module imports
// NOTHING, so `scripts/ce1-report-slot.mjs` loads it and drives the whole cross-product.
// NIKA (owner, 2026-08-13) — **THE ROW IS NECESSARY AND IT IS NOT SUFFICIENT, AND THAT NARROWS ONE
// SENTENCE OF CE-1 ABOVE.** CE-1 says *"`hasRow` is the ONLY input that can return `open`"*, and it
// was right about the defect it was written for (a flag and a clock claiming a reading Postgres does
// not hold). It is superseded HERE, at its own declaration rather than overridden below it
// (DL-2/DT-2b/R181), because the opposite failure is live and larger: **a row Postgres DOES hold,
// with no reading written into it.**
//
// **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180): 229 SAVED BIRTH CHARTS CARRY
// `interpreted: false` ACROSS 226 REAL MEMBERS**, and it is not one outage window — 12 were cast
// today against 72 healthy ones. `handleBirthChart` catches an interpretation failure, writes it to
// `console.error`, and saves the chart anyway with a placeholder sentence on every planet, every
// house and the overview. The placements are real; there are no words. So the shelf lists a birth
// chart, My Room's chip says `open`, and the member taps into a reading that was never written —
// which is the split truth the owner reported as *"my room does not show my birth chart"*.
//
// **PRESENCE, NEVER TRUTHINESS (R53/R136/R137), AND THE DATA FORCES THE DIRECTION.** 1,959 saved
// rows carry NO `interpreted` key at all — every Human Design reading, every chant, spell, petition
// and Magic Room working — and every one of them is a complete reading. So **ABSENT IS WRITTEN** and
// only an explicit `false` is the engine having failed underneath a saved row. Reading absence as
// unwritten would mark 1,959 good readings as broken, which is R19's loud direction wearing a fix.
export function readingIsWritten(interpreted?: boolean | null): boolean {
  return interpreted !== false;
}

export type ReportSlotState = "open" | "building" | "invite" | "missing" | "unwritten";

export function reportSlotState(
  hasRow: boolean,
  building: boolean,
  firstTap: boolean,
  // **DEFAULTED TO WRITTEN, AND THAT IS THE SAFE DIRECTION RATHER THAN A CONVENIENCE** (FREE-1's
  // `action` default, ITEM 3's `enforceBlocks`): a caller that says nothing keeps exactly today's
  // behaviour, so the failure mode of forgetting this argument is the world before this round and
  // never something worse.
  written: boolean = true,
): ReportSlotState {
  // THE ROW IS ASKED FIRST AND IT IS STILL THE ONLY THING THAT CAN CLAIM A READING — but a row whose
  // engine never answered is its own fact (R18), not an `open` one and not a `missing` one. Telling
  // her it is missing would be false (it is right there, with her real placements and her wheel in
  // it); telling her it is open is what shipped.
  if (hasRow && !written) return "unwritten";
  if (hasRow) return "open";
  // A build genuinely in flight. Never `firstTap` — Human Design is not written at signup, so a
  // signup window says nothing about it.
  if (building && !firstTap) return "building";
  // Never written on purpose, and its card has always said so honestly.
  if (firstTap) return "invite";
  // THE STATE THAT DID NOT EXIST, AND HER WHOLE ROUND IS THAT IT DID NOT: the house meant to write
  // this one, no build is running, and it is not here.
  return "missing";
}

// The half a presence check cannot see: WHICH states may claim a reading is there. A card is only
// allowed to look ready in `open`, and that is held as a biconditional over the whole state union so
// a fifth state added next year joins by existing rather than by somebody remembering this line.
export const REPORT_SLOT_STATES: ReportSlotState[] = ["open", "building", "invite", "missing", "unwritten"];
export function slotClaimsReading(state: ReportSlotState): boolean {
  return state === "open";
}

// CE-6's SWEEP FOUND A THIRD DEFECT AND IT IS CE-1's OWN LAW ONE QUESTION EARLIER (2026-08-10).
//
// **MEASURED: 214 `profiles` ROWS CARRY NO NAME AND NO BIRTH DATE AT ALL** — members who signed in
// and never completed room creation. The reports panel drew its strip whenever `profile` was
// truthy, and `rowToProfile` returns an OBJECT for an empty row, so all 214 were shown report chips.
// Before CE-1 those chips said *"Ready to open"*; after CE-1 they say *"This one did not arrive"*.
// **NEITHER IS TRUE FOR THEM.** Nothing was ever meant to arrive, because there is nothing to cast
// from — and *"this one did not arrive"* invites a member to report a failure that never happened.
//
// Their honest state is `reports.noProfile` — *"Create your room to generate your chart…"* — and the
// condition that picks it must be **THE BIRTH DATE, never the row's existence**. That is CE-1's law
// exactly (readiness comes from the FACT, never from a row being present), asked one question
// earlier: CE-1 asked it of the report row, and this asks it of the profile row.
//
// **THE BIRTH DATE AND NOT THE NAME**, deliberately. 7 rows carry a date and no name, and every
// reading here is cast from the DATE: a member who gave hers has begun, and blocking her on a
// missing display name would tell someone with real birth data that she has no room. The name is
// caught downstream by the generators' own `blockers`, which is where a name belongs.
//
// It lives here, beside `reportSlotState`, because this module imports NOTHING and a decision inside
// `MyRoomPage`'s thirty hooks is a decision nothing can RUN — which is how the lie CE-1 repaired sat
// in that component for months in the first place.
export function memberHasRoom(birthDate?: string | null): boolean {
  return String(birthDate || "").trim() !== "";
}

// N18-2 — **THE ARRIVAL POINTER, AND IT IS A GATE-LAW DECISION RATHER THAN A CONDITION.** It says
// her charts are ready, so it may only appear once at least one of them ACTUALLY IS: announcing
// readiness over an empty panel is the small lie the gate law exists to stop, and at signup there is
// a real window — the two are still landing — where it would have told it. It lived as a three-line
// `&&` chain inside a thirty-hook component, reading the rosters directly above it, so nothing could
// ever run it across that window. Shown ONCE per member: `reportsSeen` is profile-persisted, so a
// new device or a reinstall does not replay it.
export type ArrivalRow = { type: string; inMyRoom?: boolean | null; subject?: string | null };

// `isPersonSubject` arrives as a PARAMETER (R46's shape) because this module imports nothing — that
// is the entry requirement that lets a `.mjs` gate load and RUN it, and it is why the law is here at
// all rather than beside the predicate it needs.
export function arrivalPointerShows(
  hasProfile: boolean,
  reportsSeen: boolean,
  rows: ArrivalRow[],
  isPersonSubject: (subject?: string | null) => boolean,
): boolean {
  if (!hasProfile || reportsSeen) return false;
  // A reading SHE was given, sitting in her room, about HER — a person-subject copy is somebody
  // else's chart and says nothing about whether her own arrived.
  return signupSetReports.some((slot) => rows.some((row) =>
    row.type === slot.type && row.inMyRoom !== false && !isPersonSubject(row.subject)));
}
